import { FC, memo, useMemo } from 'react';

import { NON_BREAKING_SPACE } from 'utils/constants/common';

import {
    Button,
    Divider,
    GridRow,
    HSpacingContainer,
    TableCell,
    TableRow,
    Tag,
    Text,
    useBreakpoint,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { TagStyle } from '@hh.ru/magritte-ui-tag/types';
import { PauseOutlinedSize16, PenOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import NumberFormatter from 'bloko/common/numberFormatter';

import { PromoCode } from 'store/models/promoCodes';

export type EnrichedPromoCode = PromoCode & {
    style: TagStyle;
    statusText: string;
    boundText: string;
    periodText: string;
    remainingActivations: string;
    canBeStopped: boolean;
};

interface PromoCodeTableRowProps {
    item: EnrichedPromoCode;
    isTargeted: boolean;
    isLoading: boolean;
    onMouseEnter: (promoCode: string) => void;
    onMouseLeave: () => void;
    onStopClick: (promoCode: string) => void;
    onEditClick: (promoCode: PromoCode) => void;
}

const PromoCodeTableRowComponent: FC<PromoCodeTableRowProps> = ({
    item,
    isTargeted,
    isLoading,
    onMouseEnter,
    onMouseLeave,
    onStopClick,
    onEditClick,
}) => {
    const { canBeStopped, promoCode } = item;
    const { isMobile } = useBreakpoint();

    const action = useMemo(
        () => (
            <HSpacingContainer default={8}>
                {canBeStopped && (
                    <Button
                        size="small"
                        mode="secondary"
                        style="neutral"
                        icon={<PauseOutlinedSize16 />}
                        onClick={() => onStopClick(promoCode)}
                        loading={isLoading}
                        disabled={isLoading}
                    />
                )}
                <Button
                    size="small"
                    mode="secondary"
                    style="neutral"
                    icon={<PenOutlinedSize16 />}
                    onClick={() => onEditClick(item)}
                />
            </HSpacingContainer>
        ),
        [canBeStopped, promoCode, isLoading, onStopClick, onEditClick, item]
    );

    const cells = useMemo(() => {
        return (
            <>
                <TableCell m={isMobile ? 12 : undefined}>
                    <VSpacingContainer default={4}>
                        <Text typography="subtitle-1-semibold" style="primary">
                            {item.promoCode}
                        </Text>
                        <Text typography="label-3-regular" style="secondary">
                            {item.description}
                        </Text>
                    </VSpacingContainer>
                </TableCell>
                <TableCell m={isMobile ? 12 : undefined}>
                    <Tag style={item.style} size="medium">
                        {item.statusText}
                    </Tag>
                </TableCell>
                <TableCell m={isMobile ? 12 : undefined}>
                    <Text typography="label-3-regular" style="primary">
                        {item.periodText}
                    </Text>
                </TableCell>
                <TableCell m={isMobile ? 12 : undefined}>
                    <Text typography="label-3-regular" style="primary">
                        {NumberFormatter.format(String(item.bonus.sum), { groupSeparator: NON_BREAKING_SPACE })}
                    </Text>
                </TableCell>
                <TableCell m={isMobile ? 12 : undefined}>
                    <Text typography="label-3-regular" style="primary">
                        {NumberFormatter.format(String(item.activationsCount), {
                            groupSeparator: NON_BREAKING_SPACE,
                        })}
                    </Text>
                </TableCell>
                <TableCell m={isMobile ? 12 : undefined}>
                    <Text typography="label-3-regular" style="primary">
                        {item.remainingActivations}
                    </Text>
                </TableCell>
                <TableCell m={isMobile ? 12 : undefined}>
                    <Text typography="label-3-regular" style="secondary" maxLines={3}>
                        {item.boundText}
                    </Text>
                </TableCell>
            </>
        );
    }, [isMobile, item]);

    if (isMobile) {
        return (
            <VSpacingContainer default={12}>
                <GridRow>{cells}</GridRow>
                <Divider />
            </VSpacingContainer>
        );
    }

    return (
        <TableRow
            key={item.promoCode}
            action={action}
            showAction={isTargeted}
            onMouseEnter={() => onMouseEnter(item.promoCode)}
            onMouseLeave={onMouseLeave}
        >
            {cells}
        </TableRow>
    );
};

export const PromoCodeTableRow = memo(PromoCodeTableRowComponent);
